import { useState, useCallback, lazy, Suspense } from "react";
import { Outlet } from "react-router-dom";
import { toast } from "sonner";

// Hooks
import { useTheme } from "@/hooks/useTheme";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { usePageTracking } from "@/hooks/usePageTracking";

// Data
import type { DataItem } from "@/data/types";

// UI Components
import { Toaster } from "@/components/ui/sonner";

// Layout Components
import { Nav } from "@/components/layout/Nav";
import { SiteFooter } from "@/components/layout/SiteFooter";

// Lazy load CommandPalette - only loaded when user opens it (reduces initial bundle by ~15KB)
const CommandPalette = lazy(() => 
  import("@/features/command-palette/CommandPalette").then(m => ({ default: m.CommandPalette }))
);

export function RootLayout() {
  // Track page views on route changes
  usePageTracking();

  const { theme, toggleTheme } = useTheme();
  const [commandOpen, setCommandOpen] = useState(false);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onCommandOpen: () => setCommandOpen(true),
    onEscape: () => setCommandOpen(false),
    isCommandOpen: commandOpen,
  });

  // Reset filters callback for command palette
  const resetFilters = useCallback(() => {
    toast.success("Filters cleared");
  }, []);

  // Handle item selection from command palette
  const handleSelectItem = useCallback((item: DataItem) => {
    toast.info(`Viewing: ${item.title}`);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[--bg-primary] transition-colors duration-300">
      {/* Toaster for notifications */}
      <Toaster position="bottom-right" />

      {/* Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full blur-[250px] ${
            theme === "dark" ? "bg-[--olive]/5" : "bg-[--olive]/3"
          }`}
        />
      </div>

      {/* Command Palette - lazy loaded on first open */}
      {commandOpen && (
        <Suspense fallback={null}>
          <CommandPalette
            open={commandOpen}
            onOpenChange={setCommandOpen}
            theme={theme}
            onToggleTheme={toggleTheme}
            onResetFilters={resetFilters}
            onSelectItem={handleSelectItem}
            onShowAll={() => toast.success("Showing all items")}
          />
        </Suspense>
      )}

      {/* Navigation with Theme Toggle - Sticky */}
      <header className="sticky top-0 z-50 bg-[--bg-primary]/80 backdrop-blur-md border-b border-[--border-primary]/50">
        <Nav theme={theme} onToggleTheme={toggleTheme} />
      </header>

      {/* Page Content */}
      <main className="relative z-10 flex-grow">
        <Outlet />
      </main>

      {/* Site Footer */}
      <footer className="relative z-10 mt-auto">
        <SiteFooter />
      </footer>
    </div>
  );
}
